import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// platformLogs — journal central des erreurs de la plateforme Zifek.
//
// Tout ce qui casse (erreur JS, promesse rejetée, réponse HTTP en échec,
// erreur d'une fonction serveur) est remonté dans `platform_error_logs`
// avec la page concernée, le message réel et un contexte exploitable.
// L'écriture est toujours « best-effort » : elle ne doit jamais casser
// la page qui signale l'erreur.
// ─────────────────────────────────────────────────────────────

export type PlatformLogLevel = 'error' | 'warn' | 'info';

export interface ReportInput {
  level?: PlatformLogLevel;
  category?: string;
  message: string;
  details?: unknown;
  source?: string;
}

const DEDUPE_WINDOW_MS = 20000;
const MAX_MESSAGE = 900;
const MAX_DETAIL_STRING = 1500;

// ─────────────────────────────────────────────────────────────
// Bruit technique : balises analytics/publicitaires, balises de mesure
// (gtag utilise fetch en mode no-cors → réponse opaque, statut 0),
// requêtes annulées et extensions navigateur. Ces événements ne sont PAS
// des pannes de la plateforme : ils ne doivent ni remplir le journal, ni
// faire baisser l'indice de santé, ni déclencher d'alerte.
// ─────────────────────────────────────────────────────────────
const NOISE_PATTERNS: RegExp[] = [
  /google-analytics\.com/i,
  /googletagmanager\.com/i,
  /analytics\.google\.com/i,
  /\/g\/collect\b/i,
  /doubleclick\.net/i,
  /googleadservices\.com/i,
  /connect\.facebook\.net/i,
  /facebook\.com\/tr\b/i,
  /hotjar\.com/i,
  /clarity\.ms/i,
  /segment\.(io|com)/i,
  /tiktok\.com/i,
  /linkedin\.com\/px/i,
  /bat\.bing\.com/i,
  // Géolocalisation IP gratuite (suggestion de devise) : un 429 signifie
  // simplement « quota atteint », ce n'est pas une panne de la plateforme.
  /ipwho\.is/i,
  /ipapi\.co/i,
  /ipinfo\.io/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
  /ResizeObserver loop/i,
  /Script error\.?$/i,
  /AbortError/i,
  /The user aborted a request/i,
  /Load failed$/i,
  /cancelled/i,
  /^HTTP 0\b/i,
];

/** Vrai si le message est du bruit technique (analytics, requête annulée, ext…). */
export function isNoiseError(message?: string | null): boolean {
  const text = String(message || '').trim();
  if (!text) return false;
  return NOISE_PATTERNS.some((re) => re.test(text));
}

const recentSignatures = new Map<string, number>();
const MAX_SIGNATURES = 400;

/** Nettoyage récursif : sérialisable, borné, sans secret. */
function sanitize(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (depth > 3) return '[…]';
  if (typeof value === 'string') {
    return value.length > MAX_DETAIL_STRING ? `${value.slice(0, MAX_DETAIL_STRING)}…` : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: (value.stack || '').slice(0, MAX_DETAIL_STRING),
    };
  }
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => sanitize(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>)
      .slice(0, 30)
      .forEach(([k, v]) => {
        if (/token|authorization|apikey|api_key|password|secret/i.test(k)) {
          out[k] = '[masqué]';
        } else {
          out[k] = sanitize(v, depth + 1);
        }
      });
    return out;
  }
  return String(value);
}

function currentPath(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname || '/';
}

function shouldSend(signature: string): boolean {
  const now = Date.now();
  const last = recentSignatures.get(signature);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  if (recentSignatures.size > MAX_SIGNATURES) recentSignatures.clear();
  recentSignatures.set(signature, now);
  return true;
}

/** Envoie une erreur dans le journal central (jamais bloquant). */
export async function reportPlatformError(input: ReportInput): Promise<void> {
  try {
    const message = String(input.message || '').trim();
    if (!message) return;
    // 🧹 On ignore le bruit technique : analytics, requêtes annulées, ext…
    if (isNoiseError(message)) return;

    const page = currentPath();
    const category = input.category || 'application';
    const signature = `${category}|${message.slice(0, 160)}|${page}`;
    if (!shouldSend(signature)) return;

    let userId: string | null = null;
    let userLabel: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      const authUser = data.session?.user;
      if (authUser) {
        userId = authUser.id;
        userLabel = authUser.email || null;
      }
    } catch {
      /* session indisponible : on journalise quand même */
    }

    const host = typeof window !== 'undefined' ? window.location.host : '';

    await supabase.from('platform_error_logs').insert({
      level: input.level || 'error',
      source: input.source || 'client',
      page,
      category,
      message: message.slice(0, MAX_MESSAGE),
      details: sanitize({ ...(typeof input.details === 'object' && input.details ? (input.details as Record<string, unknown>) : { value: input.details }), host }) || {},
      user_id: userId,
      user_label: userLabel,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 400) : '',
      url: typeof window !== 'undefined' ? window.location.href.slice(0, 800) : '',
    });

    // 🚨 Détection de pic : si cette page dépasse le seuil d'erreurs sur la
    // fenêtre configurée, on crée (ou rafraîchit) une alerte SuperAdmin.
    if ((input.level || 'error') === 'error') {
      void maybeRaiseSpikeAlert(page, category, message.slice(0, MAX_MESSAGE));
    }
  } catch {
    /* le journal ne doit jamais faire tomber l'application */
  }
}

// ─────────────────────────────────────────────────────────────
// Incidents : empreinte stable d'une erreur (pour regrouper les
// occurrences identiques : même page + même catégorie + même message).
// ─────────────────────────────────────────────────────────────

export function errorFingerprint(log: { page?: string | null; category?: string | null; message?: string | null }): string {
  const normalized = String(log.message || '')
    .replace(/HTTP\s+\d{3}/gi, 'HTTP ###')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '{id}')
    .replace(/\b\d{3,}\b/g, '{n}')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .slice(0, 220);
  return `${log.page || '/'}|${log.category || '-'}|${normalized}`;
}

// ─────────────────────────────────────────────────────────────
// Alertes automatiques (pics d'erreurs par page)
// ─────────────────────────────────────────────────────────────

export interface PlatformErrorAlert {
  id: number;
  page: string;
  sample_message: string | null;
  level: string;
  category: string | null;
  occurrences: number;
  threshold: number;
  window_minutes: number;
  acknowledged: boolean;
  acknowledged_at: string | null;
  last_seen_at: string;
  created_at: string;
}

export interface ErrorAlertConfig {
  threshold: number;
  windowMinutes: number;
  cooldownMinutes: number;
}

export const DEFAULT_ALERT_CONFIG: ErrorAlertConfig = { threshold: 5, windowMinutes: 10, cooldownMinutes: 30 };

const ALERT_SETTING_KEY = 'platform_error_alert';
let cachedConfig: { value: ErrorAlertConfig; at: number } | null = null;
const spikeCheckedAt = new Map<string, number>();
const SPIKE_THROTTLE_MS = 60000;

/** Lit le seuil d'alerte configuré par le SuperAdmin. */
export async function loadErrorAlertConfig(force = false): Promise<ErrorAlertConfig> {
  if (!force && cachedConfig && Date.now() - cachedConfig.at < 120000) return cachedConfig.value;
  try {
    const { data } = await supabase
      .from('core_settings')
      .select('value')
      .eq('setting_key', ALERT_SETTING_KEY)
      .limit(1)
      .maybeSingle();
    const raw = (data?.value || {}) as Record<string, unknown>;
    const readNum = (value: unknown): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };
    const parsed: ErrorAlertConfig = {
      threshold: Math.max(1, readNum(raw.threshold) ?? DEFAULT_ALERT_CONFIG.threshold),
      windowMinutes: Math.max(1, readNum(raw.window_minutes ?? raw.windowMinutes) ?? DEFAULT_ALERT_CONFIG.windowMinutes),
      cooldownMinutes: Math.max(0, readNum(raw.cooldown_minutes ?? raw.cooldownMinutes) ?? DEFAULT_ALERT_CONFIG.cooldownMinutes),
    };
    cachedConfig = { value: parsed, at: Date.now() };
    return parsed;
  } catch {
    return DEFAULT_ALERT_CONFIG;
  }
}

/** Enregistre le seuil d'alerte (réservé au SuperAdmin). */
export async function saveErrorAlertConfig(config: ErrorAlertConfig): Promise<void> {
  const payload = {
    threshold: Math.max(1, Math.trunc(config.threshold)),
    window_minutes: Math.max(1, Math.trunc(config.windowMinutes)),
    cooldown_minutes: Math.max(0, Math.trunc(config.cooldownMinutes)),
  };
  const { data } = await supabase
    .from('core_settings')
    .select('id')
    .eq('setting_key', ALERT_SETTING_KEY)
    .limit(1)
    .maybeSingle();
  if (data?.id) {
    const { error } = await supabase.from('core_settings').update({ value: payload }).eq('id', data.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('core_settings').insert({
      level: 'platform',
      setting_key: ALERT_SETTING_KEY,
      value: payload,
    });
    if (error) throw error;
  }
  cachedConfig = { value: await loadErrorAlertConfig(true), at: Date.now() };
}

/** Liste les alertes (par défaut celles encore ouvertes). */
export async function fetchErrorAlerts(onlyOpen = true, limit = 20): Promise<PlatformErrorAlert[]> {
  let query = supabase
    .from('platform_error_alerts')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (onlyOpen) query = query.eq('acknowledged', false);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as PlatformErrorAlert[];
}

export async function acknowledgeErrorAlert(id: number): Promise<void> {
  const { error } = await supabase
    .from('platform_error_alerts')
    .update({ acknowledged: true, acknowledged_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function acknowledgeAllErrorAlerts(): Promise<void> {
  const { error } = await supabase
    .from('platform_error_alerts')
    .update({ acknowledged: true, acknowledged_at: new Date().toISOString() })
    .eq('acknowledged', false);
  if (error) throw error;
}

/** Crée ou rafraîchit une alerte lorsque le seuil d'erreurs d'une page est dépassé. */
async function maybeRaiseSpikeAlert(page: string, category: string, message: string): Promise<void> {
  try {
    const lastCheck = spikeCheckedAt.get(page);
    if (lastCheck && Date.now() - lastCheck < SPIKE_THROTTLE_MS) return;
    spikeCheckedAt.set(page, Date.now());

    const cfg = await loadErrorAlertConfig();
    const since = new Date(Date.now() - cfg.windowMinutes * 60000).toISOString();
    const { count } = await supabase
      .from('platform_error_logs')
      .select('id', { count: 'exact', head: true })
      .eq('page', page)
      .eq('level', 'error')
      .gte('created_at', since);
    if (!count || count < cfg.threshold) return;

    const cooldownSince = new Date(Date.now() - cfg.cooldownMinutes * 60000).toISOString();
    const { data: openAlert } = await supabase
      .from('platform_error_alerts')
      .select('id, occurrences')
      .eq('page', page)
      .eq('acknowledged', false)
      .gte('created_at', cooldownSince)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (openAlert?.id) {
      await supabase
        .from('platform_error_alerts')
        .update({
          occurrences: count,
          last_seen_at: new Date().toISOString(),
          sample_message: message,
          category,
        })
        .eq('id', openAlert.id);
    } else {
      await supabase.from('platform_error_alerts').insert({
        page,
        sample_message: message,
        level: 'error',
        category,
        occurrences: count,
        threshold: cfg.threshold,
        window_minutes: cfg.windowMinutes,
        last_seen_at: new Date().toISOString(),
      });
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('zifek:platform-alert'));
    }
  } catch {
    /* la détection ne doit jamais bloquer l'application */
  }
}

let installed = false;
let reportingFromFetch = false;

/** Installe la capture globale (une seule fois par session). */
export function installPlatformErrorCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    if (!event) return;
    const err = event.error instanceof Error ? event.error : null;
    void reportPlatformError({
      level: 'error',
      category: 'javascript',
      source: 'global',
      message: err?.message || event.message || 'Erreur JavaScript inconnue',
      details: {
        file: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: err?.stack,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event?.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : (() => {
              try {
                return JSON.stringify(reason);
              } catch {
                return 'Promesse rejetée sans détail';
              }
            })();
    void reportPlatformError({
      level: 'error',
      category: 'promise',
      source: 'global',
      message: message || 'Promesse rejetée',
      details: reason instanceof Error ? { stack: reason.stack } : { reason },
    });
  });

  // Réponses HTTP en échec (données, fonctions serveur, fichiers…).
  type FetchInput = Parameters<typeof window.fetch>[0];
  type FetchInit = Parameters<typeof window.fetch>[1];
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (request: FetchInput, init?: FetchInit) => {
    const response = await originalFetch(request, init);
    try {
      if (!response.ok && !reportingFromFetch) {
        const url =
          typeof request === 'string'
            ? request
            : request instanceof URL
              ? request.toString()
              : (request as Request).url || '';
        const method = (init?.method || (request as Request)?.method || 'GET').toUpperCase();
        const shortUrl = url.split('?')[0];
        const isLogWrite = shortUrl.includes('/platform_error_logs');
        const isAuthProbe = /\/auth\/v1\/(user|token)/.test(shortUrl) && response.status === 401;
        // Balises de mesure (GA, pixels, gtag…) : fetch no-cors → réponse
        // opaque, statut 0 et ok=false. Ce n'est jamais une panne Zifek.
        const isOpaque = response.status === 0 || response.type === 'opaque';
        const isNoiseUrl = isNoiseError(shortUrl);
        // Sondes HEAD non authentifiées (compteurs, pré-vérifications) :
        // elles échouent normalement avant connexion, ce n'est pas un incident.
        const isHeadProbe = method === 'HEAD' && (response.status === 401 || response.status === 403);
        if (!isLogWrite && !isAuthProbe && !isOpaque && !isNoiseUrl && !isHeadProbe) {
          reportingFromFetch = true;
          void reportPlatformError({
            level: response.status >= 500 ? 'error' : 'warn',
            category: 'network',
            source: 'http',
            message: `HTTP ${response.status} — ${method} ${shortUrl || 'requête inconnue'}`,
            details: {
              status: response.status,
              statusText: response.statusText,
              method,
              endpoint: shortUrl,
            },
          }).finally(() => {
            reportingFromFetch = false;
          });
        }
      }
    } catch {
      /* ignore */
    }
    return response;
  };
}